/* ProTech Solutions: product-only native-save watermarking.
 * Only rendered image copies change. No bot detection, feed or metadata edits.
 * Native Save Image, Copy Image and image dragging use the composited image.
 */
(function () {
  'use strict';
  var script = document.currentScript;
  var watermarkURL = script && script.getAttribute('data-protech-watermark');
  if (!watermarkURL || window.ProTechProductWatermark) return;

  var states = new WeakMap();
  var registered = new WeakSet();
  var tracked = new Set();
  var queue = [];
  var active = 0;
  var watermarkPromise;
  var pumpPending = false;
  var copies = new Map();
  var copyBytes = 0;
  var imageSelector = [
    '.product-image img', '.product-media.media-image img',
    '.product-image-thumb img', '.product-img img', 'img.product-img-item',
    '.cart-item .product-thumbnail img', '.search-result .img img',
    '.predictive-search .img img', '.bought-together .product-item .image img',
    '.more-color-wrapper .variant-option img', 'img.pswp__img',
    'img.cart-notification-product__image', '.recent-bought-inner > img'
  ].join(',');
  var resizeTimer;
  var observer;
  var intersection;
  var observing = { attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'media', 'data-src', 'data-srcset'], childList: true, subtree: true };

  document.documentElement.style.setProperty('--protech-watermark-image', 'url("' + watermarkURL.replace(/"/g, '%22') + '")');

  function absolute(value) {
    if (!value) return '';
    try { return new URL(value, document.baseURI).href; } catch (error) { return ''; }
  }

  function usable(value) {
    return value && /^(https?:|data:image\/)/i.test(absolute(value)) &&
      !/\.svg(?:[?#]|$)|^data:image\/svg\+xml/i.test(value) && absolute(value) !== absolute(watermarkURL);
  }

  function capture(image) {
    var picture = image.parentElement && image.parentElement.tagName === 'PICTURE' ? image.parentElement : null;
    var local = /^blob:/i.test(image.getAttribute('src') || '');
    return {
      src: (local ? image.getAttribute('data-protech-original-src') : image.getAttribute('src')) || '',
      srcset: image.getAttribute('srcset') || (local ? image.getAttribute('data-protech-original-srcset') : '') || '',
      deferred: image.getAttribute('data-src') || '',
      deferredSet: image.getAttribute('data-srcset') || '',
      sources: picture ? Array.prototype.map.call(picture.querySelectorAll('source'), function (source) {
        return { element: source, srcset: source.getAttribute('srcset') || source.getAttribute('data-protech-original-srcset') || '', media: source.getAttribute('media') || '' };
      }) : [],
      url: '', source: '', token: 0, status: 'idle', queued: false, visible: false
    };
  }

  function candidate(srcset, image) {
    if (!srcset || /^data:/i.test(srcset)) return '';
    var ratio = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    var width = image.getBoundingClientRect().width || image.width || 360;
    var target = Math.max(1, Math.ceil(width * ratio));
    var entries = srcset.split(',').map(function (part) {
      var match = part.trim().match(/^(\S+)(?:\s+(\d*\.?\d+)(w|x))?$/);
      return match ? { url: match[1], value: match[2] ? parseFloat(match[2]) : 1, unit: match[3] || 'x' } : null;
    }).filter(Boolean).sort(function (a, b) { return a.value - b.value; });
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].value >= (entries[i].unit === 'w' ? target : ratio)) return entries[i].url;
    }
    return entries.length ? entries[entries.length - 1].url : '';
  }

  function sourceFor(image, state) {
    for (var i = 0; i < state.sources.length; i++) {
      var source = state.sources[i];
      if (!source.media || window.matchMedia(source.media).matches) {
        var pictureSource = candidate(source.srcset, image);
        if (usable(pictureSource)) return absolute(pictureSource);
      }
    }
    var value = candidate(state.srcset, image) || state.src;
    if (!usable(value)) value = candidate(state.deferredSet, image) || state.deferred;
    return usable(value) ? absolute(value) : '';
  }

  function frameFor(image) {
    return image.closest('image-srcset, .product-media.media-image');
  }

  function updateFrame(image) {
    var frame = frameFor(image);
    if (!frame) return;
    var pending = Array.prototype.some.call(frame.querySelectorAll('img'), function (img) {
      var state = states.get(img);
      return state && state.status !== 'done' && state.status !== 'ignored';
    });
    frame.classList.toggle('protech-watermark-pending', pending);
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      var timer = setTimeout(function () { finish(new Error('Image timed out')); }, 20000);
      function finish(error) {
        clearTimeout(timer); img.onload = img.onerror = null;
        if (error) { img.src = ''; reject(error); } else resolve(img);
      }
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () { finish(img.naturalWidth && img.naturalHeight ? null : new Error('Empty image')); };
      img.onerror = function () { finish(new Error('Image unavailable')); };
      img.src = url;
    });
  }

  function loadWatermark() {
    if (!watermarkPromise) watermarkPromise = loadImage(watermarkURL).catch(function (error) {
      watermarkPromise = null; throw error;
    });
    return watermarkPromise;
  }

  function idle() {
    return new Promise(function (resolve) {
      if ('requestIdleCallback' in window) window.requestIdleCallback(resolve, { timeout: 1200 });
      else setTimeout(resolve, 32);
    });
  }

  function reusableImage(source, image) {
    // Responsive naturalWidth can be density-adjusted; decode those files once
    // separately so native saves retain their actual pixel dimensions.
    if (image.hasAttribute('srcset') || (image.parentElement && image.parentElement.tagName === 'PICTURE')) return false;
    if (!image.complete || !image.naturalWidth || absolute(image.currentSrc || image.src) !== source) return false;
    try { return new URL(source).origin === location.origin || image.crossOrigin === 'anonymous'; }
    catch (error) { return false; }
  }

  function trimCopies() {
    while (copies.size > 24 || copyBytes > 16 * 1024 * 1024) {
      var key = copies.keys().next().value;
      var entry = copies.get(key);
      if (!entry || !entry.bytes) break;
      copyBytes -= entry.bytes;
      copies.delete(key);
    }
  }

  function preparedCopy(source, image) {
    var limit = 4096;
    var key = source;
    var existing = copies.get(key);
    if (existing) { copies.delete(key); copies.set(key, existing); return existing.promise; }
    var entry = { bytes: 0, promise: null };
    entry.promise = composite(source, image, limit).then(function (blob) {
      if (blob && copies.has(key)) { entry.bytes = blob.size; copyBytes += blob.size; trimCopies(); }
      return blob;
    }).catch(function (error) { copies.delete(key); throw error; });
    copies.set(key, entry);
    return entry.promise;
  }

  async function composite(source, renderedImage, limit) {
    var loaded = await Promise.all([
      reusableImage(source, renderedImage) ? Promise.resolve(renderedImage) : loadImage(source),
      loadWatermark()
    ]);
    var image = loaded[0], watermark = loaded[1];
    if (image.naturalWidth <= 4 || image.naturalHeight <= 4) return null;
    await idle();
    // A product variant can change while waiting for an idle frame.
    if (image === renderedImage && absolute(image.currentSrc || image.src) !== source) image = await loadImage(source);
    var canvas = document.createElement('canvas');
    var scale = Math.min(1, limit / Math.max(image.naturalWidth, image.naturalHeight));
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    var context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas unavailable');
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    // Match Car Audio Zone: cover-crop the supplied transparent artwork.
    var cover = Math.max(canvas.width / watermark.naturalWidth, canvas.height / watermark.naturalHeight);
    var width = watermark.naturalWidth * cover, height = watermark.naturalHeight * cover;
    context.drawImage(watermark, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
    return new Promise(function (resolve, reject) {
      try {
        canvas.toBlob(function (blob) {
          canvas.width = canvas.height = 0;
          if (blob) resolve(blob); else reject(new Error('Image export failed'));
        }, 'image/webp', 0.95);
      } catch (error) { reject(error); }
    });
  }

  function observe() { observer.observe(document.documentElement, observing); }

  function writeCopy(image, state, url) {
    // Do not treat our own source changes as product/variant changes.
    observer.disconnect();
    try {
      image.setAttribute('data-protech-original-src', state.src);
      image.setAttribute('data-protech-original-srcset', state.srcset);
      image.removeAttribute('srcset');
      state.sources.forEach(function (source) {
        source.element.setAttribute('data-protech-original-srcset', source.srcset);
        source.element.removeAttribute('srcset');
      });
      image.src = url;
      image.setAttribute('data-protech-watermarked', 'true');
    } finally { observe(); }
  }

  async function processImage(image) {
    var state = states.get(image);
    if (!state || !image.isConnected) return false;
    var source = sourceFor(image, state);
    if (!source) { state.status = 'ignored'; updateFrame(image); return false; }
    if (state.status === 'done' && state.source === source && image.getAttribute('src') === state.url) return true;
    if (state.status === 'processing' && state.source === source) return false;
    var token = ++state.token;
    state.source = source; state.status = 'processing'; updateFrame(image);
    try {
      var blob = await preparedCopy(source, image);
      mutations(observer.takeRecords());
      if (token !== state.token || !image.isConnected) return false;
      if (!blob) { state.status = 'ignored'; updateFrame(image); return false; }
      if (sourceFor(image, state) !== source) { schedule(image, true); return false; }
      var url = URL.createObjectURL(blob);
      var previousURL = state.url;
      state.url = url; state.status = 'done';
      writeCopy(image, state, url);
      if (previousURL) URL.revokeObjectURL(previousURL);
      updateFrame(image);
      return true;
    } catch (error) {
      if (token === state.token) { state.status = 'fallback'; updateFrame(image); }
      return false;
    }
  }

  function pump() {
    while (active < 1 && queue.length) {
      var image = queue.shift();
      var state = states.get(image);
      if (!state) continue;
      state.queued = false;
      active++;
      processImage(image).finally(function () { active--; requestPump(false); });
    }
  }

  function requestPump(immediate) {
    if (immediate && !active) { pump(); return; }
    if (pumpPending || active || !queue.length) return;
    pumpPending = true;
    var run = function () { pumpPending = false; pump(); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1500 });
    else setTimeout(run, 40);
  }

  function schedule(image, immediate) {
    var state = states.get(image);
    if (!state || (!immediate && !state.visible)) return;
    // Let the theme finish loading its own visible image before doing extra work.
    if (!immediate && (!image.complete || !image.naturalWidth)) return;
    if (state.queued) {
      if (!immediate) return;
      queue.splice(queue.indexOf(image), 1);
    }
    state.queued = true;
    if (immediate) queue.unshift(image); else queue.push(image);
    requestPump(immediate);
  }

  function register(image) {
    if (!(image instanceof HTMLImageElement) || !image.matches(imageSelector) || registered.has(image) || image.hasAttribute('data-protech-no-watermark')) return;
    if (/^(blob:|data:)/i.test(image.getAttribute('src') || '') && !image.hasAttribute('data-src') && !image.hasAttribute('data-protech-original-src')) return;
    registered.add(image); tracked.add(image);
    var state = capture(image);
    states.set(image, state);
    image.removeAttribute('data-protech-watermarked');
    state.onLoad = function () {
      if (image.getAttribute('src') !== state.url) schedule(image, false);
    };
    image.addEventListener('load', state.onLoad);
    if (intersection) intersection.observe(image);
    else { state.visible = true; schedule(image, false); }
  }

  function scan(root) {
    if (!root) return;
    if (root.nodeType === 1 && root.matches && root.matches(imageSelector)) register(root);
    if (root.querySelectorAll) root.querySelectorAll(imageSelector).forEach(register);
  }

  function syncImage(image, attribute) {
    if (!image.matches(imageSelector)) return;
    if (!registered.has(image)) register(image);
    var state = states.get(image);
    if (!state) return;
    var src = image.getAttribute('src') || '';
    if (attribute === 'src' && src !== state.url && src !== state.src && !/^blob:/i.test(src) && !image.getAttribute('srcset')) {
      state.srcset = '';
    }
    if (src !== state.url && !/^blob:/i.test(src)) state.src = src;
    if (attribute === 'srcset') state.srcset = image.getAttribute('srcset') || '';
    state.deferred = image.getAttribute('data-src') || '';
    state.deferredSet = image.getAttribute('data-srcset') || '';
    if (state.status === 'done' && image.getAttribute('src') === state.url && sourceFor(image, state) === state.source) return;
    state.token++; state.status = 'idle';
    updateFrame(image); schedule(image, false);
  }

  function mutations(records) {
    records.forEach(function (record) {
      if (record.type === 'childList') {
        record.addedNodes.forEach(scan);
        record.removedNodes.forEach(function (removed) {
          var images = removed.nodeType === 1 && removed.tagName === 'IMG' ? [removed] : (removed.querySelectorAll ? removed.querySelectorAll(imageSelector) : []);
          Array.prototype.forEach.call(images, function (image) {
            if (image.isConnected) return;
            var state = states.get(image);
            if (state) {
              state.token++;
              image.removeEventListener('load', state.onLoad);
              if (state.url) {
                URL.revokeObjectURL(state.url);
                image.src = state.src;
                if (state.srcset) image.setAttribute('srcset', state.srcset);
                state.sources.forEach(function (source) { if (source.srcset) source.element.setAttribute('srcset', source.srcset); });
                image.removeAttribute('data-protech-watermarked');
              }
            }
            if (intersection) intersection.unobserve(image);
            tracked.delete(image);
            registered.delete(image);
            states.delete(image);
          });
        });
      } else if (record.target.tagName === 'IMG') {
        syncImage(record.target, record.attributeName);
      } else if (record.target.tagName === 'SOURCE') {
        var picture = record.target.parentElement;
        var image = picture && picture.querySelector('img');
        var state = image && states.get(image);
        if (!state) return;
        var source = state.sources.find(function (entry) { return entry.element === record.target; });
        if (source) {
          if (record.attributeName === 'srcset') source.srcset = record.target.getAttribute('srcset') || '';
          source.media = record.target.getAttribute('media') || '';
          state.token++; state.status = 'idle'; schedule(image, false);
        }
      }
    });
  }

  observer = new MutationObserver(mutations);
  observe();
  if ('IntersectionObserver' in window) {
    intersection = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var state = states.get(entry.target);
        if (!state) return;
        state.visible = entry.isIntersecting;
        if (entry.isIntersecting) schedule(entry.target, false);
      });
    }, { rootMargin: '0px' });
  }

  // Normal native menus and dragging remain available once a copy is ready.
  ['contextmenu', 'dragstart'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var image = event.target;
      if (!(image instanceof HTMLImageElement)) return;
      var state = states.get(image);
      if (!state || state.status === 'ignored') return;
      if (state.status === 'done' && image.getAttribute('src') === state.url) return;
      event.preventDefault(); schedule(image, true);
    }, true);
  });

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      tracked.forEach(function (image) {
        var state = states.get(image);
        if (state && state.visible && sourceFor(image, state) !== state.source) schedule(image, false);
      });
    }, 160);
  }, { passive: true });
  // Keep blob URLs alive across back/forward cache navigation.
  window.addEventListener('pagehide', function (event) {
    if (event.persisted) return;
    tracked.forEach(function (image) {
      var state = states.get(image);
      if (state && state.url) URL.revokeObjectURL(state.url);
    });
  });

  window.ProTechProductWatermark = { refresh: function (root) { scan(root || document); }, process: function (image) { register(image); schedule(image, true); } };
  scan(document);
}());
